"use client";

import { useState } from "react";
import { MapPin, Phone, Mail, Clock, Facebook, Twitter, Linkedin, Instagram } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Dialog, DialogContent, DialogTitle, DialogClose } from "@/components/ui/dialog";
import { Link } from "@/components/common/Link";
function ContactFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-4 bg-accent-light/20 border border-accent rounded-md">
        <p className="text-accent-dark text-sm">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" size="sm" className="mt-3 w-full" aria-label="Send another message">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <div className="space-y-3">
        <FormField control={control} name="name" rules={{
        required: "Name is required",
        minLength: {
          value: 2,
          message: "Min 2 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs text-foreground-primary">Name</FormLabel>
              <FormControl>
                <Input placeholder="Your Name" {...field} className="bg-background/10 border-border-strong text-foreground-primary placeholder:text-foreground-primary/50 focus:bg-background/20 transition-all duration-300" aria-label="Your name" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs text-foreground-primary">Email</FormLabel>
              <FormControl>
                <Input type="email" placeholder="you@example.com" {...field} className="bg-background/10 border-border-strong text-foreground-primary placeholder:text-foreground-primary/50 focus:bg-background/20 transition-all duration-300" aria-label="Your email address" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="message" rules={{
        required: "Message is required",
        minLength: {
          value: 10,
          message: "Min 10 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-xs text-foreground-primary">Message</FormLabel>
              <FormControl>
                <textarea placeholder="Your message..." {...field} rows={3} className="w-full bg-background/10 border border-border-strong text-foreground-primary placeholder:text-foreground-primary/50 focus:bg-background/20 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent transition-all duration-300" aria-label="Your message" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {submitError && <div className="p-3 bg-red-50 border border-red-200 rounded-md" role="alert">
            <p className="text-red-800 text-xs">{submitError}</p>
          </div>}

        <Button type="submit" disabled={isSubmitting} className="w-full bg-accent hover:bg-accent-hover text-foreground-accent transition-all duration-300" aria-label={isSubmitting ? "Sending message" : "Send message"}>
          {isSubmitting ? <>
              <span className="animate-spin mr-2" aria-hidden="true">⟳</span>
              Sending...
            </> : 'Send Message'}
        </Button>
      </div>
    </Form>;
}
export default function ContactUs() {
  const [isMapOpen, setIsMapOpen] = useState(false);
  const socialLinks = [{
    icon: Facebook,
    href: "https://www.instagram.com/romerowelding/",
    label: "Facebook - Follow Romero Welding Inc."
  }, {
    icon: Twitter,
    href: "https://www.instagram.com/romerowelding/",
    label: "Twitter - Follow Romero Welding Inc."
  }, {
    icon: Linkedin,
    href: "https://www.instagram.com/romerowelding/",
    label: "LinkedIn - Connect with Romero Welding Inc."
  }, {
    icon: Instagram,
    href: "https://www.instagram.com/romerowelding/",
    label: "Instagram - Follow Romero Welding Inc."
  }];
  return <footer className="bg-dark-background text-foreground-primary py-16 px-6">
      <div className="container mx-auto max-w-7xl">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          {}
          <div className="space-y-6" data-index={0}>
            <div>
              <h3 className="text-2xl font-display font-bold text-foreground-primary mb-6">Get In Touch</h3>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3 group" data-index={0}>
                  <MapPin className="size-5 text-accent mt-1 group-hover:scale-110 transition-transform duration-300" aria-hidden="true" />
                  <div>
                    <p className="text-sm font-semibold text-foreground-primary">Address</p>
                    <p className="text-sm text-foreground-primary/80">1901 Laguna Canyon Road, Ste 8, Laguna Beach, CA 92651</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 group" data-index={1}>
                  <Phone className="size-5 text-accent mt-1 group-hover:scale-110 transition-transform duration-300" aria-hidden="true" />
                  <div>
                    <p className="text-sm font-semibold text-foreground-primary">Phone</p>
                    <Link to="tel:+19494944522" className="text-sm text-foreground-primary/80 hover:text-accent transition-colors duration-300" aria-label="Call Romero Welding Inc. at (949) 494-4522">
                      (949) 494-4522
                    </Link>
                  </div>
                </div>

                <div className="flex items-start gap-3 group" data-index={2}>
                  <Mail className="size-5 text-accent mt-1 group-hover:scale-110 transition-transform duration-300" aria-hidden="true" />
                  <div>
                    <p className="text-sm font-semibold text-foreground-primary">Email</p>
                    <Link to="mailto:romeroweldinginc@gmail.com" className="text-sm text-foreground-primary/80 hover:text-accent transition-colors duration-300" aria-label="Email Romero Welding Inc. at romeroweldinginc@gmail.com">
                      romeroweldinginc@gmail.com
                    </Link>
                  </div>
                </div>

                <div className="flex items-start gap-3 group" data-index={3}>
                  <Clock className="size-5 text-accent mt-1 group-hover:scale-110 transition-transform duration-300" aria-hidden="true" />
                  <div>
                    <p className="text-sm font-semibold text-foreground-primary">Hours</p>
                    <p className="text-sm text-foreground-primary/80">Mon-Fri: 8am-4pm</p>
                  </div>
                </div>
              </div>
            </div>

            {}
            <div className="pt-4">
              <p className="text-sm font-semibold text-foreground-primary mb-3">Follow Us</p>
              <div className="flex gap-3">
                {socialLinks.map((social, i) => <Link key={social.label} to={social.href} newTab className="bg-background/10 p-2.5 rounded-md hover:bg-accent hover:scale-110 transition-all duration-300 group" aria-label={social.label} data-index={i}>
                    <social.icon className="size-5 text-foreground-primary group-hover:text-foreground-accent animate-pulse group-hover:animate-none transition-colors duration-300" aria-hidden="true" />
                  </Link>)}
              </div>
            </div>
          </div>

          {}
          <div data-index={1}>
            <h3 className="text-2xl font-display font-bold text-foreground-primary mb-6">Send a Message</h3>
            <FormProvider className="w-full" formId="footer-contact-form" sectionName="contact-us-footer" defaultValues={{
            name: "",
            email: "",
            message: ""
          }} validationRules={{
            name: {
              required: true,
              minLength: 2,
              maxLength: 50
            },
            email: {
              required: true,
              pattern: "email"
            },
            message: {
              required: true,
              minLength: 10
            }
          }} submitText="Send Message" successMessage="Thank you! We'll get back to you soon.">
              <ContactFormContent />
            </FormProvider>
          </div>

          {}
          <div className="space-y-4" data-index={2}>
            <h3 className="text-2xl font-display font-bold text-foreground-primary mb-6">Find Us</h3>
            <Button variant="ghost" className="relative h-64 w-full rounded-lg overflow-hidden border-2 border-border-strong cursor-pointer group hover:shadow-strong transition-all duration-300 p-0 bg-transparent hover:bg-transparent" onClick={() => setIsMapOpen(true)} aria-label="Open map to view Romero Welding Inc. location at 1901 Laguna Canyon Road, Suite 8, Laguna Beach, CA 92651">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-accent/20 group-hover:opacity-75 transition-opacity duration-300" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <MapPin className="size-16 text-accent mx-auto mb-3 group-hover:scale-125 transition-transform duration-300" aria-hidden="true" />
                  <p className="text-foreground-primary font-semibold text-lg">Click to View Map</p>
                  <p className="text-foreground-primary/70 text-sm mt-1">
                    1901 Laguna Canyon Rd, Ste 8, Laguna Beach, CA 92651
                  </p>
                </div>
              </div>
            </Button>
            <p className="text-xs text-foreground-primary/60 text-center">
              Click the map to view our location in detail
            </p>
          </div>
        </div>

        {}
        <div className="mt-12 pt-8 border-t border-border-strong/30">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-foreground-primary/70">
              © {new Date().getFullYear()} Romero Welding Inc. All rights reserved.
            </p>
            <div className="flex gap-6">
              <Link to="/PrivacyPolicyPage" className="text-sm text-foreground-primary/70 hover:text-accent transition-colors duration-300" aria-label="Read our Privacy Policy">
                Privacy Policy
              </Link>
              <Link to="#" className="text-sm text-foreground-primary/70 hover:text-accent transition-colors duration-300" aria-label="Read our Terms of Service">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>

      {}
      <Dialog open={isMapOpen} onOpenChange={setIsMapOpen}>
        <DialogContent className="max-w-4xl w-full p-0">
          <DialogTitle className="sr-only">Location Map - Romero Welding Inc.</DialogTitle>
          <div className="relative w-full h-[600px]">
            <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3322.4!2d-117.7831!3d33.5427!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x80dcf3c8d8d8d8d8%3A0x8d8d8d8d8d8d8d8!2s1901%20Laguna%20Canyon%20Rd%20%238%2C%20Laguna%20Beach%2C%20CA%2092651!5e0!3m2!1sen!2sus!4v1234567890" width="100%" height="100%" style={{
            border: 0
          }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Interactive map showing Romero Welding Inc. location at 1901 Laguna Canyon Road, Suite 8, Laguna Beach, CA 92651" />
          </div>
          <DialogClose className="absolute top-4 right-4 bg-background/90 hover:bg-background p-2 rounded-md" aria-label="Close map dialog">
            <span className="sr-only">Close</span>
          </DialogClose>
        </DialogContent>
      </Dialog>
    </footer>;
}